import {
    BuilderTabIds, RouteNames
} from "@modules/AudienceManager/constants";

import ProgressBar from './ProgressBar/ProgressBar';

import resourceMixin from '@core/Util/Mixins/resources';
import {currentTabIdMixin} from "./Mixins";

import {createNamespacedHelpers} from 'vuex';
import {bootstrapDangerGrowl} from '@core/Util/growler';

const {mapState, mapGetters, mapActions} = createNamespacedHelpers('audienceManager/builder');

export default {
    name: 'audience-builder',
    components: {
        ProgressBar
    },
    mixins: [resourceMixin, currentTabIdMixin],
    async created() {
        //New QueryString is only provided on router enter
        //we'll lose it once navigating to tabs
        const isNew = this.$route.query.new;

        if(isNew){
            await this.loadNewAudience(this.audienceId);
        } else {
            await this.loadAudience(this.audienceId);
        }
        
        await this.loadTabsWithRetries(1);
    },
    async beforeRouteLeave(to, from, next){
        await next();
        
        //Cleanup has to take place post navigation
        await this.cleanup();
    },
    watch: {
        async isNewAudience(newValue, oldValue){
            //We only care about the direct transition between true -> false
            if(oldValue !== true || newValue !== false){
                return;
            }
            
            //No need to remove the new parameter from our querystring when no querystring is present
            if(!this.$route.query || !this.$route.query.new){
                return;
            }

            //Remove 'new' parameter from queryString to allow refreshing the page without breaking the save flow
            const routeCopy = { ...this.$route, query: {...this.$route.query}  };
            delete routeCopy.query.new;
            
            await this.$router.push(routeCopy);
        },
        async isUploadedAudience(newValue){
            if(!newValue){
                return;
            }

            //Both upon load/post upload we should navigate to overview
            //questions aren't answerable for uploaded audiences
            await this.navigateToTab(BuilderTabIds.OVERVIEW);
        }
    },
    computed: {
        ...mapState(['isNewAudience', "isUploadedAudience"]),
        ...mapGetters(['sortedTabs']),

        topMenuItems() {
            const questionTabs = this.sortedTabs.map((tab) => ({
                name: tab.name,
                component: tab.id,
                href: `#`
            }));
            
            const quickstartTab = {
                name: this.resource('AudienceManager_Builder_Quickstart_Title'),
                component: BuilderTabIds.QUICKSTART,
                href: '#'
            };

            const overviewTab = {
                name: this.resource('AudienceManager_Builder_Overview_TabTitle'),
                component: BuilderTabIds.OVERVIEW,
                href: '#'
            };

            return [
                quickstartTab, 
                ...questionTabs, 
                overviewTab
            ];
        }
    },
    methods: {
        ...mapActions(['loadAudience', 'loadNewAudience', 'loadTabs', 'cleanup']),

        /**
         * Instruct vuex store to get tabs from api and save them to the store state.
         *
         * @param {Number} maxRetries //The maximum number of retries allowed for ajax call
         */
        async loadTabsWithRetries(maxRetries){
            try {
                await this.loadTabs();
            } catch(e) {
                if(maxRetries <= 0){
                    bootstrapDangerGrowl(e);
                    return;
                }

                //If we still have retries left; retry
                await this.loadTabsWithRetries(maxRetries - 1);
            }
        },
        
        /**
         * Set the tab based on it's id.
         * @param {String} id
         */
        async navigateToTab(id) {
            //Vue router doesn't like the same route being pushed twice
            if(this.currentTabId === id){
                return;
            }
            
            //Errors should be swallowed; cancelling navigation requested via 'push' throws resulting in errors
            //at the very least our quickstart component might cancel navigation via a beforeRouteLeave guard
            try{
                await this.navigateToTabImpl(id);
            } catch (e) {}
        },
        navigateToTabImpl(id){
            switch(id) {
                case BuilderTabIds.QUICKSTART:
                    return this.$router.push({ name: RouteNames.BUILDER_QUICKSTART_TAB });
                case BuilderTabIds.OVERVIEW:
                    return this.$router.push({ name: RouteNames.BUILDER_OVERVIEW_TAB });
                default:
                    return this.$router.push({name: RouteNames.BUILDER_QUESTION_TAB, params: { tabId: id }});
            }
        }
    },
    props: {
        audienceId: {
            type: String,
            required: true
        }
    }
};